import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  Check, AlertTriangle, Heart, Leaf,
  ArrowRight, ArrowLeft, Loader2, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const DietaryProfilePage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [selectedDiet, setSelectedDiet] = useState(null);
  const [selectedAllergies, setSelectedAllergies] = useState([]);
  const [selectedDislikes, setSelectedDislikes] = useState([]);
  const [customDislikes, setCustomDislikes] = useState([]);
  const [customInput, setCustomInput] = useState("");
  const [selectedPreferences, setSelectedPreferences] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [optRes, profRes] = await Promise.all([
          axios.get(`${API}/dietary/options`),
          axios.get(`${API}/auth/dietary-profile`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setOptions(optRes.data);
        if (profRes.data) {
          setSelectedDiet(profRes.data.diet_type);
          setSelectedAllergies(profRes.data.allergies || []);
          setSelectedDislikes(profRes.data.dislikes || []);
          setCustomDislikes(profRes.data.custom_dislikes || []);
          setSelectedPreferences(profRes.data.preferences || []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [token]);

  const toggle = (list, setList, id) => {
    setList((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const addCustomDislike = () => {
    const val = customInput.trim();
    if (val && !customDislikes.includes(val)) {
      setCustomDislikes((prev) => [...prev, val]);
      setCustomInput("");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/auth/dietary-profile`,
        {
          diet_type: selectedDiet,
          allergies: selectedAllergies,
          dislikes: selectedDislikes,
          custom_dislikes: customDislikes,
          preferences: selectedPreferences,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Profil alimentaire sauvegardé !");
      navigate("/friends");
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    { title: "Votre régime", subtitle: "Comment vous alimentez-vous ?" },
    { title: "Allergies", subtitle: "Sélectionnez vos allergies" },
    { title: "Pas pour moi", subtitle: "Ce que vous n'aimez pas manger" },
    { title: "Vos goûts", subtitle: "Ce que vous adorez" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="dietary-profile-page">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Progress */}
        <div className="flex gap-1">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i <= step ? "bg-primary" : "bg-gray-200"
              }`}
            />
          ))}
        </div>

        {/* Title */}
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-charcoal">
            {steps[step].title}
          </h1>
          <p className="text-sm text-muted-foreground">{steps[step].subtitle}</p>
        </div>

        <AnimatePresence mode="wait">
          {/* Step 0: Diet Type */}
          {step === 0 && (
            <motion.div key="diet" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}>
              <div className="grid grid-cols-2 gap-3">
                {options?.diet_types?.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => setSelectedDiet(d.id)}
                    className={`p-4 rounded-2xl text-left transition-all border-2 ${
                      selectedDiet === d.id
                        ? "border-primary bg-primary/5 shadow-md"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                    data-testid={`diet-${d.id}`}
                  >
                    <p className="font-medium text-charcoal">{d.label}</p>
                    {selectedDiet === d.id && (
                      <Check className="w-4 h-4 text-primary mt-1" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 1: Allergies */}
          {step === 1 && (
            <motion.div key="allergies" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}>
              <div className="grid grid-cols-2 gap-2">
                {options?.allergies?.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => toggle(selectedAllergies, setSelectedAllergies, a.id)}
                    className={`p-3 rounded-xl text-left transition-all border-2 flex items-center gap-2 ${
                      selectedAllergies.includes(a.id)
                        ? "border-red-400 bg-red-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                    data-testid={`allergy-${a.id}`}
                  >
                    <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${
                      selectedAllergies.includes(a.id) ? "text-red-500" : "text-gray-300"
                    }`} />
                    <span className="text-sm font-medium">{a.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3 text-center">
                {selectedAllergies.length === 0 ? "Aucune allergie sélectionnée" : `${selectedAllergies.length} allergie(s)`}
              </p>
            </motion.div>
          )}

          {/* Step 2: Dislikes */}
          {step === 2 && (
            <motion.div key="dislikes" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}>
              <div className="grid grid-cols-2 gap-2">
                {options?.dislikes?.map((d) => (
                  <button
                    key={d.id}
                    onClick={() => toggle(selectedDislikes, setSelectedDislikes, d.id)}
                    className={`p-3 rounded-xl text-left transition-all border-2 ${
                      selectedDislikes.includes(d.id)
                        ? "border-amber-400 bg-amber-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <span className="text-sm font-medium">{d.label}</span>
                  </button>
                ))}
              </div>
              {/* Custom dislikes */}
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-charcoal">Autre aliment ?</p>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ex: Tomates cerises"
                    value={customInput}
                    onChange={(e) => setCustomInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addCustomDislike()}
                  />
                  <Button onClick={addCustomDislike} variant="outline" size="icon">
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
                {customDislikes.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {customDislikes.map((c) => (
                      <Badge key={c} variant="outline" className="gap-1 bg-amber-50">
                        {c}
                        <button onClick={() => setCustomDislikes((p) => p.filter((x) => x !== c))}>
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 3: Preferences */}
          {step === 3 && (
            <motion.div key="preferences" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }}>
              <div className="grid grid-cols-2 gap-2">
                {options?.preferences?.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => toggle(selectedPreferences, setSelectedPreferences, p.id)}
                    className={`p-3 rounded-xl text-left transition-all border-2 ${
                      selectedPreferences.includes(p.id)
                        ? "border-green-400 bg-green-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <span className="text-sm font-medium">{p.label}</span>
                    {selectedPreferences.includes(p.id) && (
                      <Heart className="w-3 h-3 text-green-500 inline ml-1" />
                    )}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button
            variant="ghost"
            onClick={() => setStep((s) => s - 1)}
            disabled={step === 0}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Button>

          {step < steps.length - 1 ? (
            <Button onClick={() => setStep((s) => s + 1)} className="gap-2 rounded-full">
              Suivant
              <ArrowRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={save}
              disabled={saving}
              className="gap-2 rounded-full"
              data-testid="save-dietary-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Enregistrer
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DietaryProfilePage;
